import { Platform } from '@/constants';
import { AbstractModel } from '@/models/abstractModel';
import type {
    IPlayerOnlineBettingPopupRaw,
    IPlayerOnlineBettingPopupTableRow,
    IPlayerOnlineEventPopupRaw,
    IPlayerOnlineMarketRaw,
    IPlayerOnlineMarketTableRow,
    IPlayerOnlineRow,
} from '@/types/playerOnline';

function buildMarketsTableRows(markets: Record<string, IPlayerOnlineMarketRaw>): IPlayerOnlineMarketTableRow[] {
    return Object.entries(markets ?? {}).map(([marketKey, market]) => ({
        marketKey,
        feedVersion: market.feedVersion,
        feedVersionPrev: market.feedVersionPrev,
        feedUniqData: market.feedUniqData,
        filterLine: market.filter.line,
        filterPeriodTypeId: market.filter.periodTypeId,
        filterCustomEventIds: market.filter.customEventIds,
        filterMarketId: market.filter.marketId,
        filterTradingTypeId: market.filter.tradingTypeId,
    }));
}

function normalizeBettingPopupData(data: Record<string, IPlayerOnlineBettingPopupRaw> | unknown[] | undefined): Record<string, IPlayerOnlineBettingPopupRaw> {
    if (!data || Array.isArray(data)) {
        return {};
    }
    return data;
}

function buildBettingPopupTableRows(bettingPopupData: Record<string, IPlayerOnlineBettingPopupRaw>): IPlayerOnlineBettingPopupTableRow[] {
    return Object.entries(bettingPopupData).map(([popupKey, popup]) => ({
        popupKey,
        uuid: popup.uuid,
        siteIdsRaw: popup.site_ids ?? [],
        betSelectionId: popup.bet_selection_id,
        marketId: popup.market_id,
        periodTypeId: popup.period_type_id,
        customEventId: popup.custom_event_id,
        jollywhere: popup.jollywhere,
        isLive: popup.is_live,
        score: popup.score,
        handicap: popup.handicap,
        correctScore: popup.correct_score,
    }));
}

export class PlayerOnlineItem extends AbstractModel {
    public betPopupsCount: number;
    public bettingPopupTableRows: IPlayerOnlineBettingPopupTableRow[];
    public browser: string;
    public connectionId: number;
    public eventPopupTableRows: IPlayerOnlineEventPopupRaw[];
    public favEventsCount: number;
    public favoriteCustomEventIds: number[];
    public hasExpandedContent: boolean;
    public ip: string | null;
    public isTradeballUser: boolean;
    public marketsTableRows: IPlayerOnlineMarketTableRow[];
    public os: string;
    public pageCode: string;
    public pageUrl: string;
    public rawRow: IPlayerOnlineRow;
    public scriptVersion: string;
    public startAt: string;
    public uniqAppId: string;
    public updateUserDate: string | null;
    public userDisplayLabel: string;
    public userId: number;
    public userLogin: string;
    public userName: string;
    public userPlatformId: number;

    constructor(data: IPlayerOnlineRow) {
        super();
        this.connectionId = data.connectionId;
        this.userId = data.user.id;
        this.userLogin = data.user.login;
        this.userPlatformId = data.user.platform_id;
        this.userDisplayLabel = `${data.user.login} (#${data.user.id})`;
        this.isTradeballUser = data.user.platform_id === Platform.Tradeball;
        this.userName = [data.user.name, data.user.surname].filter(Boolean).join(' ') || data.user.login;
        this.ip = data.user.last_login_from_ip;
        this.uniqAppId = data.uniqAppId;
        this.scriptVersion = data.scriptVersion;
        this.os = data.os;
        this.browser = data.browser;
        this.pageUrl = data.pageUrl;
        this.pageCode = data.pageCode;
        this.startAt = data.startAt;
        this.updateUserDate = data.updateUserDate ?? null;
        this.marketsTableRows = buildMarketsTableRows(data.markets ?? {});
        this.bettingPopupTableRows = buildBettingPopupTableRows(normalizeBettingPopupData(data.bettingPopupData));
        this.eventPopupTableRows = data.eventPopupData && typeof data.eventPopupData === 'object' ? [data.eventPopupData] : [];
        this.favoriteCustomEventIds = data.favoriteCustomEventIds ?? [];
        this.betPopupsCount = this.bettingPopupTableRows.length;
        this.favEventsCount = this.favoriteCustomEventIds.length;
        this.hasExpandedContent = this.marketsTableRows.length > 0 || this.bettingPopupTableRows.length > 0 || this.eventPopupTableRows.length > 0;
        this.rawRow = data;
    }
}
